import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

export const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '0px',
  padding: '20px',
  flexWrap: 'nowrap',
  borderRadius: "12px",
  overflow: "hidden",
  height: '70vh',
  boxSizing: 'border-box',
});

export const Block = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'blockPosition',
})<{ blockPosition?: 'left' | 'middle' | 'right' }>(({ theme, blockPosition }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    paddingBottom: '40px',
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flex: '1 1 0%',
    minHeight: '0',
    overflowY: 'auto',
    boxShadow: 'none',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[1],
    },
    ...(blockPosition === 'left' && {
      borderTopLeftRadius: '12px',
      borderBottomLeftRadius: '12px',
    }),
    ...(blockPosition === 'right' && {
      borderTopRightRadius: '12px',
      borderBottomRightRadius: '12px',
    }),
  };
});

export const MiddleDivider = styled('div')(({ theme }) => ({
  width: '1px',
  height: '20%',
  backgroundColor: theme.palette.divider,
  margin: 'auto 0',
}));

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  fontWeight: '600',
  marginBottom: '12px',
  textAlign: 'left',
  color: theme.palette.text.primary,
}));

export const UserItem = styled(Typography)<{ selected: boolean }>(({ selected, theme }) => ({
  cursor: 'pointer',
  padding: '10px',
  textAlign: 'left',
  borderRadius: '6px',
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  transition: 'background-color 0.3s ease',
  '&:hover, &:focus': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const CategoryItem = styled(UserItem)({
  fontWeight: '500',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingRight: '8px',
});

export const SubcategoryItem = styled(UserItem)<{ selected: boolean }>(({ selected, theme }) => ({
  paddingLeft: '30px',
  marginTop: '6px',
  backgroundColor: selected ? theme.palette.action.hover : 'transparent',
  fontWeight: selected ? 'bold' : 'normal',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  fontSize: '0.95rem',
  display: 'flex',
  alignItems: 'center',
  height: '40px',
}));

export const SubcategoryItemText = styled(Box)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  width: '100%',
});

export const CollapseContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

export const ScrollableCategoryList = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

export const InfoText = styled(Typography)(({ theme }) => ({
  marginBottom: '10px',
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
}));

export const PermissionsPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  marginTop: theme.spacing(1),
  overflow: 'hidden',
  overflowY: 'auto',
  textOverflow: 'ellipsis',
}));

export const ElementItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '6px 0',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '4px',
  },
}));

export const EntityGroup = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const EntityGroupTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const EntityName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const BlockTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
}));

export const BlockHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1em',
}));

export const UserBlockForm = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1em',
  marginTop: '1em',
}));

export const UserNameRow = styled(Box)<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75em 1em',
  borderRadius: '0.5em',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  border: '2px solid',
  borderColor: selected ? theme.palette.primary.main : 'transparent'
}));

export const StyledUserItem = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));